import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScrapingJobService } from '@/services/scraping-job.service';
import { format } from 'date-fns';

export const ScrapingJobStatus: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: job, isLoading } = useQuery({
    queryKey: ['scrapingJob', id],
    queryFn: () => id ? ScrapingJobService.getScrapingJob(id) : Promise.reject('No ID'),
    enabled: !!id,
    refetchInterval: (query) => {
      const job = query.state.data;
      return job && (job.status === 'pending' || job.status === 'running') ? 3000 : false;
    },
  });

  useEffect(() => {
    if (job && (job.status === 'pending' || job.status === 'running')) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['scrapingJob', id] });
        queryClient.invalidateQueries({ queryKey: ['scrapingJobs'] });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [job, id, queryClient]);

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!job) {
    return <div className="text-center py-8">Job not found</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/sales/scraping-jobs')}
          className="text-blue-600 hover:text-blue-700 text-sm mb-4"
        >
          ← Back to Scraping Jobs
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Scraping Job Status</h1>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500">Search Query</label>
            <p className="mt-1 text-lg text-gray-900">{job.searchQuery}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Location</label>
            <p className="mt-1 text-lg text-gray-900">{job.location}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Source</label>
            <p className="mt-1 text-lg text-gray-900">
              {job.source === 'both' ? 'Google Maps + Yelp' : job.source}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <p className="mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                job.status === 'completed' ? 'bg-green-100 text-green-800' :
                job.status === 'running' ? 'bg-blue-100 text-blue-800' :
                job.status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {job.status}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Results</label>
            <p className="mt-1 text-lg text-gray-900">{job.resultsCount || 0}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Created</label>
            <p className="mt-1 text-gray-900">{format(job.createdAt, 'MMM d, yyyy h:mm a')}</p>
          </div>
        </div>

        {job.status === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-sm text-yellow-800">
              ⏳ Job is queued and waiting to start. This usually takes a few seconds. 
              The page will automatically refresh when the job starts.
            </p>
          </div>
        )}

        {job.status === 'running' && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              🔄 Job is running. Scraping restaurants from {job.source === 'both' ? 'Google Maps and Yelp' : job.source}... 
              This may take 5-10 minutes depending on the number of results.
            </p>
          </div>
        )}

        {job.status === 'completed' && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-800">
              ✅ Job completed! Found {job.resultsCount} restaurants. 
              They have been added to the approval queue.
            </p>
            <button
              onClick={() => navigate('/sales/approval-queue')}
              className="mt-3 inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
            >
              View in Approval Queue
            </button>
          </div>
        )}

        {job.status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800">
              ❌ Job failed: {job.error || 'Unknown error'}
            </p>
            <p className="text-xs text-red-600 mt-2">
              Check Cloud Function logs for more details: firebase functions:log
            </p>
          </div>
        )}

        {job.error && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Error Details</label>
            <pre className="text-xs text-gray-600 whitespace-pre-wrap">{job.error}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

